import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import axios from "axios";
import "./RegiftPage.css";

const RegiftPage = () => {
  const { giftId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [gift, setGift] = useState(null);
  const [recipient, setRecipient] = useState({ name: "", email: "" });
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Fetch gift details on component mount
  useEffect(() => {
    const fetchGiftDetails = async () => {
      try {
        setLoading(true);

        // Fetch gift details
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/gifts/received`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // Find the specific gift by ID
        const foundGift = response.data.gifts.find((g) => g._id === giftId);

        if (!foundGift) {
          setError(
            "Gift not found or you do not have permission to regift it."
          );
          return;
        }

        setGift(foundGift);
      } catch (error) {
        console.error("Error fetching gift details:", error);
        setError(
          error.response?.data?.message ||
            "Failed to load gift details. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    if (token && giftId) {
      fetchGiftDetails();
    }
  }, [token, giftId]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!recipient.email) {
      setError("Recipient email is required.");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipient.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      // Enhanced CSRF token handling
      console.log("Fetching CSRF token for regift...");

      // Log available cookies before request
      console.log("Cookies before request:", document.cookie);

      // Fetch CSRF token with improved error handling
      const csrfResponse = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/csrf-token`,
        {
          withCredentials: true,
        }
      );

      console.log("CSRF response:", csrfResponse.data);

      // Log cookies after request
      console.log("Cookies after request:", document.cookie);

      // Enhanced function to get CSRF token from cookies
      const getCsrfTokenFromCookie = () => {
        const cookies = document.cookie.split(";");
        console.log("Parsing cookies:", cookies);

        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split("=");
          console.log(`Checking cookie: ${name} = ${value}`);

          // Check for both possible cookie names (case insensitive)
          if (name.toLowerCase() === "xsrf-token") {
            return decodeURIComponent(value);
          }
        }
        return null;
      };

      // Try to get token from cookie first
      let csrfToken = getCsrfTokenFromCookie();

      // If cookie approach fails, use the token from the response
      if (!csrfToken && csrfResponse.data && csrfResponse.data.csrfToken) {
        console.log("Using CSRF token from response body instead of cookie");
        csrfToken = csrfResponse.data.csrfToken;
      }

      if (!csrfToken) {
        console.error("CSRF token not found in cookies or response");
        throw new Error(
          "Failed to obtain CSRF token. Please refresh the page and try again."
        );
      }

      console.log("Using CSRF token:", csrfToken);

      // Create regift request with enhanced error handling
      const requestData = {
        recipientEmail: recipient.email,
        recipientName: recipient.name,
        note,
        _csrf: csrfToken, // Include token in request body
      };

      console.log("Sending regift request for gift ID:", giftId);

      // Add the token to multiple headers to ensure compatibility
      const headers = {
        Authorization: `Bearer ${token}`,
        "X-CSRF-Token": csrfToken,
        "CSRF-Token": csrfToken,
        "X-XSRF-TOKEN": csrfToken,
        "XSRF-TOKEN": csrfToken,
      };

      console.log("Using headers for regift request:", headers);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/gifts/regift/${giftId}`,
        requestData,
        {
          headers,
          withCredentials: true,
        }
      );

      console.log("Regift successful:", response.data);

      setSuccess(true);

      // Redirect to member dashboard after 3 seconds
      setTimeout(() => {
        navigate("/member");
      }, 3000);
    } catch (error) {
      console.error("Error regifting:", error);

      // Enhanced error handling
      let errorMessage = "Failed to regift. Please try again.";

      if (error.response) {
        if (
          error.response.status === 403 &&
          error.response.data.error === "CSRF_ERROR"
        ) {
          errorMessage =
            "Security validation failed. Please refresh the page and try again.";
        } else {
          errorMessage = error.response.data.message || errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel button
  const handleCancel = () => {
    navigate("/member");
  };

  // Render loading state
  if (loading) {
    return (
      <div className="regift-page">
        <div className="loading-spinner"></div>
        <p>Loading gift details...</p>
      </div>
    );
  }

  // Render error state
  if (error && !gift) {
    return (
      <div className="regift-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button
            onClick={() => navigate("/member")}
            className="primary-button"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Render success state
  if (success) {
    return (
      <div className="regift-page">
        <div className="success-container">
          <div className="success-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M0 0h24v24H0V0z" fill="none" />
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm4.59-12.42L10 14.17l-2.59-2.58L6 13l4 4 8-8z" />
            </svg>
          </div>
          <h2>Gift Successfully Regifted!</h2>
          <p>Your gift has been sent to {recipient.name || recipient.email}.</p>
          <p>Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="regift-page">
      <div className="regift-container">
        <h1>Regift Your Credits</h1>

        {gift && (
          <div className="gift-details">
            <h2>Gift Details</h2>
            <div className="gift-detail-item">
              <span className="detail-label">Session Type:</span>
              <span className="detail-value">
                {gift.displayName
                  ? gift.displayName
                  : gift.giftType.includes("shared")
                  ? "Flexible Session Credits"
                  : gift.giftType.includes("sauna")
                  ? "Infrared Sauna"
                  : "Red Light Therapy"}
              </span>
            </div>
            <div className="gift-detail-item">
              <span className="detail-label">Number of Sessions:</span>
              <span className="detail-value">{gift.sessionCount}</span>
            </div>
            <div className="gift-detail-item">
              <span className="detail-label">Originally From:</span>
              <span className="detail-value">
                {gift.sender
                  ? `${gift.sender.firstName} ${gift.sender.lastName}`
                  : "A friend"}
              </span>
            </div>
          </div>
        )}

        <form className="regift-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="recipient-email">Recipient Email *</label>
            <input
              type="email"
              id="recipient-email"
              value={recipient.email}
              onChange={(e) =>
                setRecipient({ ...recipient, email: e.target.value })
              }
              required
              placeholder="Enter recipient's email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="recipient-name">Recipient Name (Optional)</label>
            <input
              type="text"
              id="recipient-name"
              value={recipient.name}
              onChange={(e) =>
                setRecipient({ ...recipient, name: e.target.value })
              }
              placeholder="Enter recipient's name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="gift-note">Personal Note (Optional)</label>
            <textarea
              id="gift-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a personal message"
              maxLength={200}
            />
            <div className="note-character-count">
              {note.length}/200 characters
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={submitting}
            >
              {submitting ? "Processing..." : "Regift Now"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegiftPage;
